import React, { useState, useEffect, useMemo } from "react";

import { useAuth } from "../../../Context/AuthContext";
import { fetchBookFormats } from "../../../Api/Backend/BookFormat";
import { fetchProductionCosts } from "../../../Api/Backend/ProductionCost";

// Helper function to find the closest page count key
const getPageKey = (pages) => {
  const pageSteps = [200, 250, 300, 350, 400, 450, 500];
  const numPages = Number(pages);

  let closestStep = pageSteps.reduce((prev, curr) => {
    return curr <= numPages && curr > prev ? curr : prev;
  }, 0);

  return closestStep;
};

function BookProduction({
  numberOfPages,
  onCostChange,
  onProductionInfoChange,
  services,
  setServices,
}) {
  const auth = useAuth();
  const [bookFormats, setBookFormats] = useState([]);
  const [productionCosts, setProductionCosts] = useState([]);

  const [productionInfo, setProductionInfo] = useState({
    bookFormatId: "",
    bookFormatSize: "",
    bookFormatDescription: "",
    color: "",
    printRunSelected: false,
    numberOfCopies: "1000",
  });

  const [localCost, setLocalCost] = useState({ printing: 0, paper: 0 });

  useEffect(() => {
    if (bookFormats.length > 0 && !productionInfo.bookFormatId) {
      setProductionInfo((prev) => ({
        ...prev,
        bookFormatId: bookFormats[0].bookFormatId,
        bookFormatSize: bookFormats[0].size,
        bookFormatDescription: bookFormats[0].description,
      }));
    }
  }, [bookFormats]);

  const handleLocalChange = (e) => {
    const { name, value, type, checked } = e.target;

    const newInfo = {
      ...productionInfo,
      [name]: type === "checkbox" ? checked : value,
    };

    if (name === "bookFormatId") {
      const selectedFormat = bookFormats.find(
        (format) => String(format.bookFormatId) === value
      );
      if (selectedFormat) {
        newInfo.bookFormatSize = selectedFormat.size;
        newInfo.bookFormatDescription = selectedFormat.description;
      }
    }

    setProductionInfo(newInfo);
  };

  useEffect(() => {
    if (!productionInfo.printRunSelected || !productionInfo.bookFormatId) {
      setLocalCost({ printing: 0, paper: 0 });
      return;
    }

    const pageKey = getPageKey(numberOfPages);
    const colorKey = productionInfo.color.startsWith("1도") ? "1도" : "4도";
    const copiesKey = parseInt(productionInfo.numberOfCopies);
    const formatIdKey = parseInt(productionInfo.bookFormatId);

    const priceData = productionCosts.find(
      (cost) =>
        cost.bookFormatId === formatIdKey &&
        cost.numberOfPages === pageKey &&
        cost.color === colorKey &&
        cost.numberOfCopies === copiesKey
    );

    if (priceData) {
      setLocalCost({
        printing: priceData.printingCost,
        paper: priceData.paperCost,
      });
    } else {
      setLocalCost({ printing: 0, paper: 0 });
    }
  }, [productionInfo, numberOfPages, productionCosts]);

  useEffect(() => {
    onCostChange(
      productionInfo.printRunSelected ? localCost : { printing: 0, paper: 0 }
    );
    onProductionInfoChange(productionInfo);
  }, [localCost, productionInfo, onCostChange, onProductionInfoChange]);

  const availableColors = useMemo(() => {
    if (!productionInfo.bookFormatId) return [];

    const formatId = parseInt(productionInfo.bookFormatId);
    const uniqueColors = new Set();

    productionCosts.forEach((cost) => {
      if (cost.bookFormatId === formatId) {
        uniqueColors.add(cost.color);
      }
    });

    return Array.from(uniqueColors);
  }, [productionInfo.bookFormatId, productionCosts]);

  const availableCopyOptions = useMemo(() => {
    if (!productionInfo.bookFormatId || !productionInfo.color) return [];

    const formatId = parseInt(productionInfo.bookFormatId);
    const color = productionInfo.color;
    const copies = new Set();

    productionCosts.forEach((cost) => {
      if (cost.bookFormatId === formatId && cost.color === color) {
        copies.add(cost.numberOfCopies);
      }
    });

    return Array.from(copies).sort((a, b) => a - b);
  }, [productionInfo.bookFormatId, productionInfo.color, productionCosts]);

  useEffect(() => {
    // Reset color if not available for selected bookFormat
    if (productionInfo.bookFormatId && availableColors.length > 0) {
      if (!availableColors.includes(productionInfo.color)) {
        setProductionInfo((prev) => ({
          ...prev,
          color: availableColors[0],
        }));
      }
    }
  }, [availableColors]);

  useEffect(() => {
    // Reset numberOfCopies if not valid for selected bookFormat and color set
    if (
      productionInfo.bookFormatId &&
      productionInfo.color &&
      availableCopyOptions.length > 0
    ) {
      if (
        !availableCopyOptions.includes(parseInt(productionInfo.numberOfCopies))
      ) {
        setProductionInfo((prev) => ({
          ...prev,
          numberOfCopies: String(availableCopyOptions[0]),
        }));
      }
    }
  }, [availableCopyOptions]);

  useEffect(() => {
    fetchBookFormats({ onSuccess: setBookFormats });
    fetchProductionCosts({ onSuccess: setProductionCosts });
  }, []);

  useEffect(() => {
    if (!setServices) return; // Guard against props not being ready

    setServices((currentServices) => {
      if (!currentServices || currentServices.length === 0) return [];

      return currentServices.map((service) => {
        if (service.title === "본문 내지 디자인") {
          // Determine the price based on the selected color
          const newPrice = productionInfo.color.startsWith("4도")
            ? 7000
            : service.originalPrice; // Revert to original price if not 4-color

          return { ...service, unitPrice: newPrice };
        }
        return service;
      });
    });
  }, [productionInfo.color, setServices]);

  return (
    <div className="service-category-block">
      <div className="qt-category-header">도서 제작</div>
      <div className="service-items-container">
        <div className="qt-production-row">
          <div className="qt-cell-v2 check-cell"></div>
          <div className="qt-cell-v2 item-name">판형</div>
          <div className="qt-cell-v2 production-control">
            <select
              name="bookFormatId"
              value={productionInfo.bookFormatId}
              onChange={handleLocalChange}
            >
              <option value="" disabled>
                판형을 선택해 주세요
              </option>
              {bookFormats.map((format) => (
                <option key={format.bookFormatId} value={format.bookFormatId}>
                  {format.size} - {format.description}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="qt-production-row">
          <div className="qt-cell-v2 check-cell"></div>
          <div className="qt-cell-v2 item-name">본문 컬러</div>
          <div className="qt-cell-v2 production-control">
            <select
              name="color"
              value={productionInfo.color}
              onChange={handleLocalChange}
            >
              {availableColors.map((colorOption) => (
                <option key={colorOption} value={colorOption}>
                  {colorOption}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="qt-row-v2 print-run-row-inline">
          <div className="qt-cell-v2 check-cell">
            <input
              type="checkbox"
              id="printRunCheckInline"
              name="printRunSelected"
              checked={productionInfo.printRunSelected}
              onChange={handleLocalChange}
            />
          </div>
          <div className="qt-cell-v2 item-name">
            <label htmlFor="printRunCheckInline">인쇄부수</label>
          </div>

          <div className="qt-cell-v2 qty-group">
            <select
              name="numberOfCopies"
              value={productionInfo.numberOfCopies}
              onChange={handleLocalChange}
              className="qty-select"
              disabled={!productionInfo.printRunSelected}
            >
              {availableCopyOptions.map((copy) => (
                <option key={copy} value={copy}>
                  {copy}
                </option>
              ))}
            </select>
            <span className="unit-label">부</span>
            <input
              type="number"
              className="qty-input"
              value={numberOfPages}
              readOnly
              disabled
            />
            <span className="unit-label">페이지</span>
          </div>
          <div className="qt-cell-v2"></div>

          <div className="qt-cell-v2 text-right production-cost">
            <span>인쇄비</span>
            <span>용지비</span>
          </div>

          <div className="qt-cell-v2 text-right production-cost">
            <span> {localCost.printing.toLocaleString()}</span>
            <span>{localCost.paper.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookProduction;
